import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import {
  useGetKBArticles,
  useGetKBArticleDetail,
  invalidateKBArticles,
} from 'actions/chatbot';
import ArticleList from './article-list';
import EditForm from './edit-form';
import { Container, LeftPanel, RightPanel, EmptyState } from './styled-comps';

function KnowledgeBaseTab({ botConfig }) {
  const dispatch = useDispatch();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    get: getArticles,
    data: articlesData,
    isProcessing: loading,
    error: articlesError,
  } = useGetKBArticles(botConfig);

  const { get: getArticleDetail, data: articleDetailData } = useGetKBArticleDetail();

  const articles = Array.isArray(articlesData) ? articlesData : [];

  const fetchArticles = useCallback(() => {
    invalidateKBArticles(botConfig);
    getArticles(botConfig);
  }, [getArticles, botConfig]);

  useEffect(() => {
    if (articleDetailData) {
      setSelectedArticle(articleDetailData);
    }
  }, [articleDetailData]);

  const handleSelectVersion = version => {
    if (version && version.id) {
      getArticleDetail(version.id);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    if (articlesError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch KB articles',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [articlesError, dispatch]);

  const handleSelect = article => {
    setSelectedArticle(article);
    // Refetch detail to ensure we have the latest content (article from
    // the grouped list is the "head" row — PUBLISHED/DRAFT — but may be
    // stale if this tab has been open for a while).
    if (article && article.id) {
      getArticleDetail(article.id);
    }
  };

  const handleNew = () => {
    setSelectedArticle({ title: '', scope: '', content: '', status: 'DRAFT' });
  };

  const handleSaved = () => {
    fetchArticles();
    if (selectedArticle && selectedArticle.id) {
      getArticleDetail(selectedArticle.id);
    } else {
      setSelectedArticle(null);
    }
  };

  return (
    <Container>
      <LeftPanel>
        {loading && !Array.isArray(articles) ? (
          <EmptyState>Loading...</EmptyState>
        ) : loading && articles.length === 0 ? (
          <EmptyState>Loading...</EmptyState>
        ) : (
          <ArticleList
            articles={Array.isArray(articles) ? articles : []}
            selectedKey={selectedArticle?.article_key}
            onSelect={handleSelect}
            onNew={handleNew}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}
      </LeftPanel>
      <RightPanel>
        {selectedArticle ? (
          <EditForm
            article={selectedArticle}
            onSaved={handleSaved}
            onSelectVersion={handleSelectVersion}
            botConfig={botConfig}
          />
        ) : (
          <EmptyState>Select an article or create a new one</EmptyState>
        )}
      </RightPanel>
    </Container>
  );
}

export default KnowledgeBaseTab;
